/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {Logger} from '@app/lib/Logger';
import LocalVoiceStateStore from '@app/stores/LocalVoiceStateStore';
import VoiceSettingsStore from '@app/stores/VoiceSettingsStore';
import MediaEngineStore from '@app/stores/voice/MediaEngineFacade';
import {getSoundboardSoundURL} from '@app/utils/SoundboardUtils';

const logger = new Logger('SoundboardPlayer');

export const SOUNDBOARD_DATA_TOPIC = 'soundboard';

interface SoundboardDataMessage {
	t: 'play';
	sound_id: string;
	guild_id?: string;
	volume?: number;
}

let audioContext: AudioContext | null = null;
const bufferCache = new Map<string, AudioBuffer>();

const clamp01 = (value: number): number => Math.min(Math.max(value, 0), 1);

function getAudioContext(): AudioContext {
	if (!audioContext) {
		audioContext = new AudioContext();
	}
	return audioContext;
}

async function loadBuffer(soundId: string): Promise<AudioBuffer | null> {
	const cached = bufferCache.get(soundId);
	if (cached) {
		return cached;
	}

	const url = getSoundboardSoundURL(soundId);
	if (!url) {
		return null;
	}

	try {
		const response = await fetch(url);
		if (!response.ok) {
			logger.warn('Failed to fetch soundboard sound', {soundId, status: response.status});
			return null;
		}
		const arrayBuffer = await response.arrayBuffer();
		const ctx = getAudioContext();
		const decoded = await ctx.decodeAudioData(arrayBuffer);
		bufferCache.set(soundId, decoded);
		return decoded;
	} catch (error) {
		logger.warn('Failed to load soundboard sound', {soundId, error});
		return null;
	}
}

export async function playSoundboardSoundLocally(soundId: string, volume: number): Promise<void> {
	// Deafened users should not hear soundboard playback (matches voice deafen semantics).
	if (LocalVoiceStateStore.getSelfDeaf()) {
		return;
	}

	const buffer = await loadBuffer(soundId);
	if (!buffer) {
		return;
	}

	try {
		const ctx = getAudioContext();
		if (ctx.state === 'suspended') {
			await ctx.resume().catch(() => {});
		}

		const source = ctx.createBufferSource();
		source.buffer = buffer;
		const gain = ctx.createGain();
		// Scale by this listener's local soundboard volume preference.
		gain.gain.value = clamp01(volume) * clamp01(VoiceSettingsStore.getSoundboardVolume());
		source.connect(gain);
		gain.connect(ctx.destination);
		source.start();
		source.addEventListener(
			'ended',
			() => {
				try {
					source.disconnect();
					gain.disconnect();
				} catch {}
			},
			{once: true},
		);
	} catch (error) {
		logger.warn('Failed to play soundboard sound', {soundId, error});
	}
}

export async function triggerSoundboardSound(params: {
	soundId: string;
	guildId: string;
	volume: number;
}): Promise<void> {
	const {soundId, guildId, volume} = params;
	const room = MediaEngineStore.room;

	if (room?.localParticipant) {
		try {
			const message: SoundboardDataMessage = {t: 'play', sound_id: soundId, guild_id: guildId, volume};
			const payload = new TextEncoder().encode(JSON.stringify(message));
			await room.localParticipant.publishData(payload, {reliable: true, topic: SOUNDBOARD_DATA_TOPIC});
		} catch (error) {
			logger.warn('Failed to publish soundboard data', {soundId, error});
		}
	}

	await playSoundboardSoundLocally(soundId, volume);
}

export async function handleSoundboardData(payload: Uint8Array): Promise<void> {
	let message: SoundboardDataMessage;
	try {
		message = JSON.parse(new TextDecoder().decode(payload)) as SoundboardDataMessage;
	} catch {
		return;
	}

	if (message?.t === 'play' && typeof message.sound_id === 'string') {
		const volume = typeof message.volume === 'number' ? message.volume : 1;
		await playSoundboardSoundLocally(message.sound_id, volume);
	}
}
